<template>
  <div class="reset-password-page">
    <VContainer fluid class="fill-height">
      <VRow align="center" justify="center">
        <VCol cols="12" sm="6" md="4" lg="3">
          <VCard elevation="2">
            <VCardTitle class="mb-4 reset-title"> Réinitialiser le mot de passe </VCardTitle>
            <VCardText>
              <form @submit.prevent="submit">
                <VTextField
                  v-model="newPassword"
                  label="Nouveau mot de passe"
                  :type="showPassword ? 'text' : 'password'"
                  :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append-inner="showPassword = !showPassword"
                  :error-messages="passwordErrors"
                  @blur="validateNewPassword"
                  variant="outlined"
                  density="comfortable"
                  class="mb-1"
                />
                <p class="text-caption text-medium-emphasis mb-4">
                  Au moins 8 caractères, avec majuscule, minuscule, chiffre et caractère spécial.
                </p>

                <VTextField
                  v-model="confirmPassword"
                  label="Confirmer le nouveau mot de passe"
                  :type="showPassword ? 'text' : 'password'"
                  variant="outlined"
                  density="comfortable"
                  class="mb-4"
                />

                <VCardActions>
                  <VBtn
                    type="submit"
                    color="primary"
                    :loading="callingAPI"
                    :disabled="callingAPI"
                    elevation="4"
                    block
                  >
                    Réinitialiser le mot de passe
                  </VBtn>
                </VCardActions>
              </form>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </VContainer>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAppStore } from "@/store/app";
import { postResetPassword } from "@/utils/user-api";

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();

const token = (route.query.token as string) ?? "";

const newPassword = ref("");
const confirmPassword = ref("");
const showPassword = ref(false);
const callingAPI = ref(false);
const passwordErrors = ref<string[]>([]);

const passwordRules: Array<(v: string) => string | true> = [
  (v) => v.length >= 8 || "Au moins 8 caractères",
  (v) => /[a-z]/.test(v) || "Au moins une minuscule",
  (v) => /[A-Z]/.test(v) || "Au moins une majuscule",
  (v) => /[0-9]/.test(v) || "Au moins un chiffre",
  (v) => /[^A-Za-z0-9]/.test(v) || "Au moins un caractère spécial",
];

const validateNewPassword = () => {
  passwordErrors.value = passwordRules
    .map((rule) => rule(newPassword.value))
    .filter((result) => typeof result === "string") as string[];
};

const submit = async () => {
  if (!token) {
    appStore.sendErrorNotice("Invalid or missing reset token.");
    return;
  }

  if (!newPassword.value || !confirmPassword.value) {
    appStore.sendErrorNotice("All fields are required.");
    return;
  }

  if (newPassword.value !== confirmPassword.value) {
    appStore.sendErrorNotice("Passwords do not match.");
    return;
  }

  validateNewPassword();
  if (passwordErrors.value.length) {
    appStore.sendErrorNotice("Password is not strong enough.");
    return;
  }

  callingAPI.value = true;
  try {
    await postResetPassword({ token, newPassword: newPassword.value });
    appStore.sendSuccessNotice("Password reset successfully. You can now log in.");
    router.push("/login");
  } catch (error) {
    appStore.sendErrorNotice("Failed to reset password. The link may have expired.");
  } finally {
    callingAPI.value = false;
  }
};
</script>

<style scoped>
.fill-height {
  min-height: 100vh;
}

.reset-title {
  background-color: rgb(var(--v-theme-primary));
  color: white;
  font-weight: 600;
  padding: 16px;
}

.mb-4 {
  margin-bottom: 1rem;
}

.reset-password-page {
  min-height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
